/**
 * StreamSettings — standalone settings screen for stream deposit/rate.
 *
 * Persists to localStorage so the call flow uses these values automatically.
 */

import { useState, type ReactNode } from 'react';
import { useWallet } from '../modules/wallet/WalletContext.js';
import {
  formatTokenAmount,
  getChainConfig,
} from '@tokenstreaming/shared';
import {
  loadStreamSettings,
  saveStreamSettings,
  DEFAULT_STREAM_SETTINGS,
} from '../modules/streaming/streamSettings.js';

interface Props {
  onBack: () => void;
}

export function StreamSettings({ onBack }: Props): ReactNode {
  const { balances, chainId } = useWallet();
  const chain = getChainConfig(chainId);

  const stored = loadStreamSettings();
  const [deposit, setDeposit] = useState(stored.deposit);
  const [ratePerSec, setRatePerSec] = useState(stored.ratePerSec);
  const [tokenAddress, setTokenAddress] = useState(stored.tokenAddress || chain.tokens[0]?.address || '');
  const [saved, setSaved] = useState(false);

  const selectedToken = chain.tokens.find(t => t.address === tokenAddress) ?? chain.tokens[0];
  const tokenBalance = balances.tokens.find(t => t.address === tokenAddress)?.balance ?? 0n;

  const handleSave = () => {
    saveStreamSettings({ deposit, ratePerSec, tokenAddress });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setDeposit(DEFAULT_STREAM_SETTINGS.deposit);
    setRatePerSec(DEFAULT_STREAM_SETTINGS.ratePerSec);
    setTokenAddress(chain.tokens[0]?.address || '');
  };

  // Estimate duration
  const estimatedDuration = (() => {
    try {
      const dep = parseFloat(deposit);
      const rate = parseFloat(ratePerSec);
      if (!dep || !rate || rate <= 0) return null;
      const secs = Math.floor(dep / rate);
      if (secs < 60) return `${secs}s`;
      if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
      return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
    } catch {
      return null;
    }
  })();

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <h2>Stream Settings</h2>
      </div>
      <div className="screen-body">
        <p className="text-secondary">
          Configure default deposit and rate for streaming calls.
          These values are used automatically when you start a call.
        </p>

        {/* Token selection */}
        <div className="form-group">
          <label>Token</label>
          <select value={tokenAddress} onChange={e => setTokenAddress(e.target.value)}>
            {chain.tokens.map(token => {
              const bal = balances.tokens.find(t => t.address === token.address);
              return (
                <option key={token.address} value={token.address}>
                  {token.symbol} — {bal ? formatTokenAmount(bal.balance, token.decimals) : '0'} available
                </option>
              );
            })}
          </select>
        </div>

        {/* Deposit amount */}
        <div className="form-group">
          <label>Default Deposit ({selectedToken?.symbol ?? 'TOKEN'})</label>
          <input
            type="text"
            value={deposit}
            onChange={e => setDeposit(e.target.value)}
            placeholder="0.001"
            inputMode="decimal"
          />
          <span className="form-hint">
            Balance: {formatTokenAmount(tokenBalance, selectedToken?.decimals ?? 18)} {selectedToken?.symbol ?? ''}
          </span>
        </div>

        {/* Rate */}
        <div className="form-group">
          <label>Rate ({selectedToken?.symbol ?? 'TOKEN'} per second)</label>
          <input
            type="text"
            value={ratePerSec}
            onChange={e => setRatePerSec(e.target.value)}
            placeholder="0.00001"
            inputMode="decimal"
          />
          {estimatedDuration && (
            <span className="form-hint">
              Estimated stream duration: {estimatedDuration}
            </span>
          )}
        </div>

        <div className="streaming-actions">
          <button className="btn-primary btn-full" onClick={handleSave}>
            {saved ? 'Saved!' : 'Save Settings'}
          </button>
          <button className="btn-secondary btn-full" onClick={handleReset}>
            Reset to Defaults
          </button>
        </div>
      </div>
    </div>
  );
}
